<template>
  <div
    class="vue-dropzone dropzone"
    :id="id || undefined"
    @click="openFileDialog"
  >
    <input
      ref="fileInput"
      type="file"
      class="dz-hidden-input"
      style="display: none;"
      :multiple="multiple"
      :accept="accept"
      @change="onChange"
      @click.stop
    >
    <slot />
  </div>
</template>

<script>
/**
 * Minimal vue2-dropzone compatible wrapper.
 * Exposes removeAllFiles() and .dropzone.files / .dropzone.on used across the app.
 */
export default {
  name: 'VueDropzone',
  props: {
    options: { type: Object, default: () => ({}) },
    id: { type: String, default: '' }
  },
  emits: ['vdropzone-success', 'vdropzone-error', 'vdropzone-file-added'],
  data () {
    return {
      dropzone: null
    }
  },
  computed: {
    multiple () {
      const maxFiles = this.options?.maxFiles
      return maxFiles !== 1
    },
    accept () {
      return this.options?.acceptedFiles || ''
    }
  },
  created () {
    const files = []
    const listeners = {}
    const emit = (event, ...args) => {
      ;(listeners[event] || []).forEach((handler) => {
        try {
          handler(...args)
        } catch (error) {
          console.warn(`Dropzone "${event}" handler failed:`, error)
        }
      })
    }

    this.dropzone = {
      files,
      on (event, handler) {
        if (!listeners[event]) listeners[event] = []
        listeners[event].push(handler)
      },
      off (event, handler) {
        if (!listeners[event]) return
        listeners[event] = listeners[event].filter((fn) => fn !== handler)
      },
      emit,
      removeAllFiles: () => {
        files.splice(0, files.length)
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      },
      removeFile: (file) => {
        const index = files.indexOf(file)
        if (index !== -1) files.splice(index, 1)
      }
    }
  },
  methods: {
    removeAllFiles () {
      this.dropzone.removeAllFiles()
    },
    removeFile (file) {
      this.dropzone.removeFile(file)
    },
    openFileDialog () {
      this.$refs.fileInput?.click()
    },
    onChange (event) {
      const selected = Array.from(event.target.files || [])
      const maxFiles = this.options?.maxFiles
      const remaining =
        maxFiles && Number(maxFiles) > 0
          ? Math.max(0, Number(maxFiles) - this.dropzone.files.length)
          : selected.length
      const toAdd = selected.slice(0, remaining || selected.length)

      Promise.all(toAdd.map((file) => this.prepareFile(file))).then((preparedFiles) => {
        preparedFiles.forEach((file) => {
          this.dropzone.files.push(file)
          this.dropzone.emit('addedfile', file)
          this.$emit('vdropzone-file-added', file)
        })
      })

      // Allow selecting the same file again
      event.target.value = ''
    },
    prepareFile (file) {
      return new Promise((resolve) => {
        // vue2-dropzone attaches dataURL + upload.filename for API save
        const attachMeta = (target, dataURL) => {
          try {
            Object.defineProperty(target, 'dataURL', {
              value: dataURL,
              writable: true,
              configurable: true,
              enumerable: true
            })
          } catch (error) {
            target.dataURL = dataURL
          }
          target.upload = { filename: target.name || file.name || 'image.jpg' }
          return target
        }

        if (!file.type || !file.type.startsWith('image/')) {
          file.upload = { filename: file.name || 'file' }
          resolve(file)
          return
        }
        const reader = new FileReader()
        reader.onload = () => {
          try {
            resolve(attachMeta(file, reader.result))
          } catch (error) {
            const wrapped = new File([file], file.name, {
              type: file.type,
              lastModified: file.lastModified
            })
            Object.defineProperty(wrapped, 'size', { value: file.size })
            resolve(attachMeta(wrapped, reader.result))
          }
        }
        reader.onerror = () => {
          file.upload = { filename: file.name || 'image.jpg' }
          resolve(file)
        }
        reader.readAsDataURL(file)
      })
    }
  }
}
</script>
